// src/providers/app.jsx
import { Button, Spinner } from 'components/Elements';
import { Notifications } from 'components/Notifications/Notifications';
import { AuthProvider } from 'lib/auth';
import { queryClient } from 'lib/react-query';
import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { BrowserRouter as Router } from 'react-router-dom';
import { MessagingSocketProvider } from './messaging';
import { ApiSocketProvider } from './api';
import { RideHailingSocketProvider } from './ridehailing';
import { NotificationsProvider } from './notifications';

const ErrorFallback = () => {
  const [feedback, setFeedback] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState('');

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg('');

    try {
      const subject = encodeURIComponent('Error Report');
      const body = encodeURIComponent(
        `An error occurred in the application.\n\nUser Report:\n${feedback}`
      );

      window.location.href = `mailto:jonahgeek@gmail.com?subject=${subject}&body=${body}`;

      setSubmitted(true);
    } catch (error) {
      setErrorMsg('We could not send your report. Please try again shortly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-screen h-screen flex items-center justify-center bg-gray-100 px-4">
      <div className="bg-white shadow-lg rounded-xl p-8 max-w-lg w-full border border-gray-200 animate-fadeIn">
        <h2 className="text-2xl font-semibold text-gray-900 mb-3">Something went wrong</h2>

        <p className="text-gray-600 mb-6 leading-relaxed">
          An unexpected error occurred. You can help us investigate the issue by sharing what you
          were doing before it happened.
        </p>

        {!submitted ? (
          <form onSubmit={handleFeedbackSubmit} className="space-y-5">
            <div>
              <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-2">
                What happened?
              </label>

              <textarea
                id="feedback"
                rows={5}
                className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition"
                placeholder="Describe the steps or actions leading to the issue..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                required
              />
            </div>

            {errorMsg && <p className="text-red-500 text-sm font-medium">{errorMsg}</p>}

            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={submitting} className="flex-1 h-10">
                {submitting ? 'Submitting...' : 'Submit Feedback'}
              </Button>

              <Button
                type="button"
                variant="secondary"
                className="flex-1 h-10"
                onClick={() => {
                  localStorage.clear();
                  window.location.assign(window.location.origin);
                }}
              >
                Restart App
              </Button>
            </div>
          </form>
        ) : (
          <div className="text-center py-6">
            <div className="text-green-600 text-lg font-medium mb-2">
              Feedback sent successfully
            </div>
            <p className="text-gray-600">Thank you for helping us improve the platform.</p>

            <Button
              className="mt-6 w-full"
              onClick={() => window.location.assign(window.location.origin)}
            >
              Reload Application
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export const AppProvider = ({ children }) => {
  return (
    <React.Suspense
      fallback={
        <div className="flex items-center justify-center w-screen h-screen">
          <Spinner size="xl" />
        </div>
      }
    >
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            {process.env.NODE_ENV !== 'test' && <ReactQueryDevtools />}
            <Notifications />
            <AuthProvider>
              <ApiSocketProvider>
                <NotificationsProvider>
                  <MessagingSocketProvider>
                    <RideHailingSocketProvider>
                      <Router>{children}</Router>
                    </RideHailingSocketProvider>
                  </MessagingSocketProvider>
                </NotificationsProvider>
              </ApiSocketProvider>
            </AuthProvider>
          </QueryClientProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </React.Suspense>
  );
};
